import { useMemo, useState } from "react";
import { useStore } from "@/context/StoreContext";
import { useTheme } from "@/context/ThemeContext";
import { STAGES, ORIGENS, stageLabel, prioLabel, origemLabel, fmtDate } from "@/lib/constants";
import { Page, Empty, ChartCanvas } from "@/components/ui-z";

const hours = (a, b) => ((new Date(b) - new Date(a)) / 36e5);

const toCsv = (rows) => {
  const head = ["Codigo", "Titulo", "Solicitante", "Origem", "OrigemRef", "Etapa", "Status", "Prioridade", "Responsavel", "AbertoEm", "ResolvidoEm"];
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  return [head.join(";"), ...rows.map((t) => [t.codigo, t.titulo, t.solicitante_nome, origemLabel(t.origem), t.origem_ref, stageLabel(t.stage), t.status, prioLabel(t.prioridade), t.assigned_to_name, fmtDate(t.created_at), fmtDate(t.resolved_at)].map(esc).join(";"))].join("\n");
};

const InfoBox = ({ bg, icon, label, value, testId }) => (
  <div className="col-12 col-sm-6 col-md-3">
    <div className="info-box" data-testid={testId}>
      <span className={`info-box-icon ${bg} elevation-1`}><i className={icon} /></span>
      <div className="info-box-content">
        <span className="info-box-text">{label}</span>
        <span className="info-box-number">{value}</span>
      </div>
    </div>
  </div>
);

export default function Relatorios() {
  const { tickets } = useStore();
  const { skin } = useTheme();
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const rows = useMemo(() => tickets.filter((t) =>
    (!from || t.created_at >= from) && (!to || t.created_at <= `${to}T23:59:59`)), [tickets, from, to]);

  const byResp = useMemo(() => {
    const m = {};
    rows.forEach((t) => {
      const k = t.assigned_to_name || "Não atribuído";
      m[k] = m[k] || { responsavel: k, total: 0, resolvidos: 0 };
      m[k].total++; if (t.status === "resolvido") m[k].resolvidos++;
    });
    return Object.values(m);
  }, [rows]);

  const resolved = rows.filter((t) => t.resolved_at);
  const avg = resolved.length ? (resolved.reduce((s, t) => s + hours(t.created_at, t.resolved_at), 0) / resolved.length).toFixed(1) : "—";

  // Opções do gráfico "Sales" do dashboard3.js (Total x Resolvidos por responsável)
  const barConfig = useMemo(() => {
    const ticks = { fontColor: skin.darkMode ? "#adb5bd" : "#495057", fontStyle: "bold" };
    return {
      type: "bar",
      data: {
        labels: byResp.map((r) => r.responsavel),
        datasets: [
          { label: "Total", backgroundColor: "#007bff", borderColor: "#007bff", data: byResp.map((r) => r.total) },
          { label: "Resolvidos", backgroundColor: "#ced4da", borderColor: "#ced4da", data: byResp.map((r) => r.resolvidos) },
        ],
      },
      options: {
        maintainAspectRatio: false,
        tooltips: { mode: "index", intersect: true },
        hover: { mode: "index", intersect: true },
        legend: { display: false },
        scales: {
          yAxes: [{ gridLines: { display: true, lineWidth: "4px", color: skin.darkMode ? "rgba(255, 255, 255, .1)" : "rgba(0, 0, 0, .2)", zeroLineColor: "transparent" }, ticks: { beginAtZero: true, precision: 0, ...ticks } }],
          xAxes: [{ display: true, gridLines: { display: false }, ticks }],
        },
      },
    };
  }, [byResp, skin.darkMode]);

  const exportCsv = () => {
    const blob = new Blob(["﻿" + toCsv(rows)], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `relatorio-chamados-${Date.now()}.csv`;
    a.click();
  };

  return (
    <Page title="Relatórios & Performance" crumbs={[["Gestão"], ["Relatórios"]]}>
      <div className="card card-default">
        <div className="card-header">
          <h3 className="card-title"><i className="far fa-calendar-alt mr-1" />Período</h3>
          <div className="card-tools">
            <button type="button" data-testid="export-csv-btn" className="btn btn-primary btn-sm mr-1" onClick={exportCsv} disabled={!rows.length}><i className="fas fa-file-csv mr-1" />Exportar CSV</button>
            <button type="button" data-testid="export-pdf-btn" className="btn btn-default btn-sm" onClick={() => window.print()}><i className="fas fa-print mr-1" />Imprimir / PDF</button>
          </div>
        </div>
        <div className="card-body pb-0">
          <div className="row">
            <div className="col-md-3 form-group">
              <label htmlFor="report-from">De</label>
              <input id="report-from" data-testid="report-from-input" type="date" className="form-control" value={from} onChange={(e) => setFrom(e.target.value)} />
            </div>
            <div className="col-md-3 form-group">
              <label htmlFor="report-to">Até</label>
              <input id="report-to" data-testid="report-to-input" type="date" className="form-control" value={to} onChange={(e) => setTo(e.target.value)} />
            </div>
          </div>
        </div>
      </div>

      <div className="row">
        <InfoBox testId="report-total" bg="bg-info" icon="fas fa-ticket-alt" label="Chamados no período" value={rows.length} />
        <InfoBox testId="report-resolved" bg="bg-success" icon="fas fa-check" label="Resolvidos" value={resolved.length} />
        <InfoBox testId="report-rate" bg="bg-warning" icon="fas fa-percent" label="Taxa de resolução" value={rows.length ? `${Math.round((resolved.length / rows.length) * 100)}%` : "—"} />
        <InfoBox testId="report-avg-time" bg="bg-danger" icon="fas fa-hourglass-half" label="Tempo médio (h)" value={avg} />
      </div>

      {rows.length === 0 ? <div className="card"><div className="card-body"><Empty testId="report-empty" text="Sem chamados no período." /></div></div> : (
        <div className="row">
          <div className="col-lg-6">
            <div className="card">
              <div className="card-header border-0"><h3 className="card-title">Desempenho por responsável</h3></div>
              <div className="card-body">
                <ChartCanvas config={barConfig} height={250} />
                <div className="d-flex flex-row justify-content-end">
                  <span className="mr-2"><i className="fas fa-square text-primary" /> Total</span>
                  <span><i className="fas fa-square text-gray" /> Resolvidos</span>
                </div>
              </div>
            </div>
          </div>
          <div className="col-lg-6">
            <div className="card">
              <div className="card-header"><h3 className="card-title">Volume por etapa e origem</h3></div>
              <div className="card-body p-0">
                <table className="table table-sm table-striped">
                  <tbody>
                    {STAGES.map((s) => <tr key={s.key}><td>{s.label}</td><td className="text-right"><span className="badge bg-primary">{rows.filter((t) => t.stage === s.key).length}</span></td></tr>)}
                    {ORIGENS.map((o) => <tr key={o.key}><td className="text-muted">Origem: {o.label}</td><td className="text-right"><span className="badge bg-secondary">{rows.filter((t) => t.origem === o.key).length}</span></td></tr>)}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}
    </Page>
  );
}
